"use client";

import { motion } from "framer-motion";
import { Bug, ChevronRight, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";

interface FeedbackChooserProps {
  onSelectBug: () => void;
  onSelectFeedback: () => void;
}

const cards = [
  {
    mode: "bug" as const,
    titleKey: "feedbackModal.chooser.bugTitle" as const,
    subtitleKey: "feedbackModal.chooser.bugSubtitle" as const,
    icon: Bug,
    accentColor: "text-red-600",
    bgColor: "bg-red-50",
    hoverBorder: "hover:border-red-300",
    hoverBg: "hover:bg-red-50/50",
  },
  {
    mode: "feedback" as const,
    titleKey: "feedbackModal.chooser.feedbackTitle" as const,
    subtitleKey: "feedbackModal.chooser.feedbackSubtitle" as const,
    icon: Lightbulb,
    accentColor: "text-[#0077FF]",
    bgColor: "bg-blue-50",
    hoverBorder: "hover:border-[#0077FF]/40",
    hoverBg: "hover:bg-blue-50/50",
  },
];

export const FeedbackChooser = ({
  onSelectBug,
  onSelectFeedback,
}: FeedbackChooserProps) => {
  const handlers = { bug: onSelectBug, feedback: onSelectFeedback };
  const t = useI18n();

  return (
    <div className="flex flex-col gap-2.5">
      {cards.map((card) => (
        <motion.button
          key={card.mode}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={handlers[card.mode]}
          className={cn(
            "flex items-start gap-3.5 rounded-xl border border-border p-4 text-left transition-colors cursor-pointer",
            card.hoverBorder,
            card.hoverBg,
          )}
        >
          <div
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-[10px]",
              card.bgColor,
            )}
          >
            <card.icon className={cn("size-5", card.accentColor)} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground">
              {t(card.titleKey)}
            </p>
            <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
              {t(card.subtitleKey)}
            </p>
          </div>
          <ChevronRight className="mt-2 size-4 shrink-0 text-muted-foreground/50" />
        </motion.button>
      ))}
    </div>
  );
};
